import { useNavigate } from 'react-router-dom';
import { Card } from '../../organisms/card';
import styles from './ScreeningOverviewCompletedStep.module.css';
import { useTranslation } from 'react-i18next';
import { StepperCircle } from '../../../components/atoms/stepper-circle';
import Text from '../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../enums';
import ButtonNew from '../button/Button';
import PencilLineIcon from '../../atoms/icons/PencilLineIcon';
import ArrowDownSLineIcon from '../../atoms/icons/ArrowDownSLineIcon';
import React, { useEffect, useRef, useState } from 'react';
import { classNames } from '../../utils';
import { Moment } from 'moment';
import { languageContext } from '../../providers/LanguageProvider';

interface ScreeningOverviewCompleteStepProps {
    title: string;
    routePath: string;
    updatedAt?: Moment;
    required?: boolean;
    children?: React.ReactNode;
}

const ScreeningOverviewCompleteStep = ({
    title,
    routePath,
    updatedAt,
    required = false,
    children
}: ScreeningOverviewCompleteStepProps) => {
    const language = languageContext();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const cardContentRef = useRef<HTMLDivElement>(null);

    const [ collapsed, setCollapsed ] = useState(false);

    useEffect(() => {
        if (cardContentRef.current) {
            cardContentRef.current.style.height = (collapsed ? '0px' : `${cardContentRef.current.scrollHeight}px`);
        }
    }, [collapsed])

    return (
        <Card className={classNames(styles.completedStepCard, collapsed && styles.collapsed)}>
            <div className={styles.header}>
                <StepperCircle checked/>
                <div className={styles.stepInformation}>
                    <Text
                        color={ColorEnum.Gray800}
                        size={FontSizeEnum.Lg}
                        weight={FontWeightEnum.Regular}
                    >
                        {title}
                        {!required && (
                            <>
                                <span>&nbsp;</span>
                                <Text
                                    color={ColorEnum.Gray400}
                                    size={FontSizeEnum.Lg}
                                    weight={FontWeightEnum.Regular}
                                >({t('optional')})</Text>
                            </>
                        )}
                    </Text>
                    {updatedAt &&
                        <Text
                            color={ColorEnum.Gray500}
                            size={FontSizeEnum.Xs}
                            weight={FontWeightEnum.Regular}
                        >
                            {t('screening.overview.step.updatedAt', { date: updatedAt.locale(language.code).format('dddd, D MMMM YYYY, HH:mm') })}
                        </Text>
                    }
                </div>
                <div className={styles.buttonContainer}>
                    <ButtonNew
                        fullWidth={true}
                        className={styles.editButton}
                        variant='secondary-gray'
                        onClick={() => navigate(routePath)}
                        iconBefore={<PencilLineIcon/>}
                    ></ButtonNew>
                    {children && (
                        <ButtonNew
                            className={classNames(styles.collapseButton, collapsed && styles.collapsed)}
                            variant='tertiary-gray'
                            iconOnly
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            <ArrowDownSLineIcon className={styles.arrowIcon}/>
                        </ButtonNew>
                    )}
                </div>
            </div>
            {children && (
                <div
                    className={classNames(styles.content, collapsed && styles.collapsed)}
                    ref={cardContentRef}
                >
                    {children}
                </div>
            )}
            <hr className={classNames(styles.isMobile, collapsed && styles.mobileEditButtonDivider)}/>
            <ButtonNew
                className={styles.mobileEditButton}
                variant='secondary-gray'
                onClick={() => navigate(routePath)}
                iconBefore={<PencilLineIcon />}
            >{t('edit')}</ButtonNew>
        </Card>
    );
}

export default ScreeningOverviewCompleteStep;
